import type {
  CircleShape,
  DrawShape,
  Shape,
  ZoneShape,
  ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import {
  createCircle,
  createRectangle,
  createZone
} from "@gettactic/map-builder/src/utils/shapeFactory";
import { normalizeRect } from "../document/bounds";
import { isPrimaryButton } from "../gesture/pointer";
import type { Point, Rect } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

const MIN_SIZE = 3;

export interface DrawToolOptions {
  /** Organisation zone new zone shapes are tagged with (`zone` tool). */
  getZoneTag: () => ZoneTypeDefinition | null;
}

const squareUp = (origin: Point, point: Point): Point => {
  const width = point.x - origin.x;
  const height = point.y - origin.y;
  const size = Math.max(Math.abs(width), Math.abs(height));
  return {
    x: origin.x + Math.sign(width || 1) * size,
    y: origin.y + Math.sign(height || 1) * size
  };
};

const shapeForRect = (
  kind: DrawShape,
  rect: Rect,
  zoneTag: ZoneTypeDefinition | null
): Shape | null => {
  switch (kind) {
    case "circle": {
      const circle = createCircle({
        x: rect.x + rect.width / 2,
        y: rect.y + rect.height / 2
      }) as CircleShape;
      circle.width = rect.width;
      circle.height = rect.height;
      circle.radius = Math.round(Math.max(rect.width, rect.height) / 2);
      return circle;
    }
    case "zone": {
      const zone = createZone({ x: rect.x, y: rect.y }) as ZoneShape;
      zone.width = rect.width;
      zone.height = rect.height;
      if (zoneTag) {
        zone.zone_id = zoneTag.id;
        zone.categoryTypeId = zoneTag.id;
        zone.name = zoneTag.name;
      }
      return zone;
    }
    case "star":
      // No star shape type exists in the document format.
      return null;
    default: {
      const rectangle = createRectangle({ x: rect.x, y: rect.y });
      rectangle.width = rect.width;
      rectangle.height = rect.height;
      return rectangle;
    }
  }
};

/**
 * Drag to draw a rectangle, circle or zone. `draw` uses the store's
 * `tool.drawShape`; the `zone` tool always draws zones and tags them.
 */
export const createDrawTool = (
  kind: "draw" | "zone",
  options: DrawToolOptions
): Tool => {
  let origin: Point | null = null;
  let context: ToolContext | null = null;

  const drawKind = (): DrawShape =>
    kind === "zone"
      ? "zone"
      : (context?.store.getState().tool.drawShape ?? "rectangle");

  const frame = createFrameScheduler<{ client: Point; shiftKey: boolean }>(
    ({ client, shiftKey }) => {
      if (!context || !origin) return;
      const raw = context.snap.snapPoint(context.toWorld(client));
      const point = shiftKey ? squareUp(origin, raw) : raw;
      const current = drawKind();
      context.overlay.set({
        draft: {
          kind:
            current === "circle"
              ? "ellipse"
              : current === "zone"
                ? "zone"
                : "rect",
          rect: normalizeRect(origin, point)
        }
      });
    }
  );

  const reset = () => {
    frame.cancel();
    origin = null;
    context?.overlay.reset();
  };

  return {
    kind,
    cursor: "crosshair",
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      origin = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!origin) return;
      frame.schedule({
        client: { x: event.clientX, y: event.clientY },
        shiftKey: event.shiftKey
      });
    },
    onPointerUp(toolContext, event) {
      context = toolContext;
      if (!origin) return;
      const raw = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      const point = event.shiftKey ? squareUp(origin, raw) : raw;
      const rect = normalizeRect(origin, point);
      const current = drawKind();
      reset();
      if (rect.width < MIN_SIZE || rect.height < MIN_SIZE) return;
      const shape = shapeForRect(
        current,
        rect,
        kind === "zone" ? options.getZoneTag() : null
      );
      if (!shape) return;
      const { store } = toolContext;
      if (shape.type === "zone" && shape.categoryTypeId) {
        const tag = options.getZoneTag();
        const zoneTypes = store.getState().meta.zoneTypes ?? [];
        if (tag && !zoneTypes.some((entry) => entry.id === tag.id)) {
          store.getState().setMeta({ zoneTypes: [...zoneTypes, tag] });
        }
      }
      store.getState().addShapes([shape], { select: current === "zone" });
    },
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && origin) {
        context = toolContext;
        reset();
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};
